'use client';

import { useEffect, useRef, memo } from 'react';

interface AuroraProps {
  colorStops?: string[];
  speed?: number;
  blur?: number;
  className?: string;
}

/**
 * Aurora - WebGL-powered animated gradient background
 * Inspired by aura.build and React Bits
 */
function AuroraComponent({
  colorStops = ['#FF6B6B', '#FF8E72', '#00D4FF'],
  speed = 0.3,
  blur = 80,
  className = '',
}: AuroraProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    let time = 0;

    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };

    resize();
    window.addEventListener('resize', resize);

    const blobs = colorStops.map((color, i) => ({
      color,
      x: width * (0.2 + i * 0.3),
      y: height * (0.3 + (i % 2) * 0.4),
      radius: Math.min(width, height) * (0.3 + i * 0.1),
      xSpeed: (i + 1) * 0.5,
      ySpeed: (i + 1) * 0.3,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      blobs.forEach((blob, i) => {
        if (!prefersReducedMotion) {
          blob.x = width * 0.5 + Math.sin(time * speed * blob.xSpeed + i) * width * 0.3;
          blob.y = height * 0.5 + Math.cos(time * speed * blob.ySpeed + i * 2) * height * 0.3;
        }

        const gradient = ctx.createRadialGradient(
          blob.x, blob.y, 0,
          blob.x, blob.y, blob.radius
        );
        gradient.addColorStop(0, blob.color + '40');
        gradient.addColorStop(0.5, blob.color + '20');
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(blob.x, blob.y, blob.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      if (!prefersReducedMotion) {
        time += 0.016;
        animationRef.current = requestAnimationFrame(draw);
      }
    };

    draw();
    if (prefersReducedMotion) {
      // Draw once for static background
      draw();
    }

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [colorStops, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ filter: `blur(${blur}px)` }}
      aria-hidden="true"
    />
  );
}

export const Aurora = memo(AuroraComponent);
